import type { OcrHeader, OcrItem, SearchResult } from '../../types/api';

/** AI-OCR仮/汎用コード(マスタ名称「AIOCR」)。マスタに無い品名や、
 * OCR読取テキストを商品名として採用した明細に付与する。バックエンドの
 * SPOT_HINCD と一致させること。 */
export const SPOT_PRODUCT_CODE = '99999950';

/** 小計(4)・中計(5)の行の商品名1。三興バルブ様要望（10/16）で区分ごとに固定の表記にする。
 * 名前に「値引」を含む行は、ERP書込みで区分4に倒されるが小計ではないため元の名前を残す。
 * サーバー側（oracle_store.py の subtotal_label）と同じ規則にすること。 */
export function subtotalLabelOf(rowType: string | undefined, name: string | undefined): string | null {
  if ((name ?? '').includes('値引')) return null;
  if (rowType === 'subtotal') return '【小　計】';
  if (rowType === 'midtotal') return '【中　計】';
  return null;
}

/** 完全な日付(YYYY-MM-DD)か判定。OCRが年だけ('2026')等の不完全値を返すことがあり、
 * type=date は空表示なのに truthy で必須を通ってしまうため、これで「未入力」扱いにする。 */
export function isCompleteDate(d?: string | null): boolean {
  return !!d && /^\d{4}-\d{2}-\d{2}$/.test(d);
}

export interface Row {
  id: string; // uuid
  line_no: number | null;
  row_type?: 'detail' | 'note' | 'subtotal' | 'midtotal'; // 見積の行種別(区分0/3/4/5)。未設定=明細
  product_name: string;
  size: string;
  quantity: string;
  unit: string;
  irisu?: string; // 入数（単位変換マスタから取得）
  kosu?: string; // 個数（入数×数量）
  note?: string;
  item_note?: string; // 明細備考1 (LINCMA)
  item_note2?: string; // 明細備考2 (LINCMB)。見積のみ
  // 備考/小計行(非明細)で手入力する商品名1/2。未設定なら品名/寸法をフォールバック
  note_name1?: string;
  note_name2?: string;
  // 見積の単価3列（任意・文字列で編集）。定価=JODTK / 見積単価=ESTTK / 原価=GNKTK
  list_price?: string;
  estimate_unit_price?: string;
  cost_price?: string;
  isAdded?: boolean;
  isDeleted?: boolean;
  afterRowId?: string; // 追加行の挿入位置（元行のID）
  /** 伝票分割: 何番目の伝票か。未設定=1。値は区切りの操作で増減し、画面・保存時は
   * 出現順に 1..N へ振り直して使う（slipInfo）。並べ替えても行と一緒に動く。 */
  slip?: number;
}

export interface MatchInfo {
  matchedId: string;
  matchedName: string;
  candidates: SearchResult[];
}

export type Phase = 'idle' | 'uploading' | 'ocr_running' | 'ocr_done' | 'saving';

export interface OcrState {
  file: File | null;
  fileName: string | null;
  previewUrl: string | null;
  sourceId: string;
  customerCode: string | null;
  customerAbbr: string | null;
  siteCode: string | null;
  siteName: string | null;
  siteAbbr: string | null;
  ocrSiteName: string | null; // OCR抽出の現場名（自動マッチ用、リセットしない）
  customerNameEditable: boolean;
  siteNameEditable: boolean;
  header: OcrHeader;
  headerOriginal: OcrHeader;
  rows: Row[];
  rowsOriginal: Row[]; // 元の行（修正検出用、IDは同じ）
  editedFields: Set<string>; // "rowId:field" or "header:field"
  matches: Record<string, MatchInfo>;
  matchesOriginal: Record<string, MatchInfo>; // 初回検索結果（変更検出用）
  confirmedRows: Set<string>;
  ocrNameRows: Set<string>; // マッチ商品名にOCR品名+寸法を使う行
  ocrNameRowsOriginal: Set<string>; // 保存時点のトグル状態（変更検出用）
  headerConfirmed: boolean;
  phase: Phase;
  // 明細の並べ替え状態（ERP書込み画面と同じUX）。null=OCR読取順。
  // 並べ替えは明細行(detail)のみが対象で、備考/小計行は位置を固定する。
  sortKey: string | null;
  sortAsc: boolean;
}

/** ERPへ書き込む商品コードを返す。画面の表示・保存時の送信値・商品コード順の並べ替えで
 * 同じ関数を使う（別実装にすると、表示と実際に送る値や並び順がずれる）。
 * 名称ﾏﾆｭｱﾙ入力区分(HINNMMKB)='1' の商品は「名称を書き換えてよい」とERP側で定義された
 * 汎用コードなので、OCR名に切り替えても選んだコードをそのまま使う（三興バルブ様確認済み）。
 * それ以外のマスタ商品は、コードと名称が食い違う伝票になるため仮コード(99999950)へ倒す。 */
export function erpProductCodeOf(
  s: Pick<OcrState, 'matches' | 'ocrNameRows'>,
  r: Row,
): string {
  const m = s.matches[r.id];
  if (!m) return '';
  if (!s.ocrNameRows.has(r.id)) return m.matchedId || '';
  const cand = m.candidates.find((c) => c.id === m.matchedId);
  return cand?.name_editable ? (m.matchedId || '') : SPOT_PRODUCT_CODE;
}

export type OcrAction =
  | { type: 'FILE_SELECTED'; file: File; previewUrl: string }
  | { type: 'UPLOAD_LOADED'; uploadId: string; fileName: string; previewUrl: string }
  | { type: 'OCR_START' }
  | { type: 'OCR_DONE'; header: OcrHeader; sourceId: string; items: OcrItem[] }
  | { type: 'OCR_ERROR' }
  | { type: 'CELL_EDIT'; rowId: string; field: keyof Row; value: string }
  | { type: 'HEADER_EDIT'; field: keyof OcrHeader; value: string }
  | { type: 'ROW_DELETE'; rowId: string }
  | { type: 'ROW_REMOVE'; rowId: string }
  | { type: 'ROW_RESTORE'; rowId: string }
  | { type: 'ROW_ADD'; afterRowId: string }
  | { type: 'ROW_TYPE_SET'; rowId: string; value: 'detail' | 'note' | 'subtotal' | 'midtotal' }
  | { type: 'CORRECTIONS_SAVED' }
  | { type: 'CUSTOMER_SET'; code: string; name: string; abbr?: string; staffCode?: string; nameEditable?: boolean }
  | { type: 'SITE_SET'; code: string; name?: string; abbr?: string; nameEditable?: boolean }
  | { type: 'HEADER_CONFIRM' }
  | { type: 'MATCH_SET'; rowId: string; match: MatchInfo }
  | { type: 'ROW_CONFIRM'; rowId: string }
  | { type: 'TOGGLE_OCR_NAME'; rowId: string }
  | { type: 'SYNC_OCR_NAME_ORIGINAL' }
  | { type: 'SORT_ROWS'; key: string }
  | { type: 'MOVE_ROW'; rowId: string; direction: 'up' | 'down' }
  | { type: 'MOVE_ROW_TO'; rowId: string; targetId: string }
  | { type: 'SLIP_SPLIT'; rowId: string }
  | { type: 'SLIP_MERGE'; rowId: string }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_DONE' }
  | { type: 'RESET' };

const uuid = () => Math.random().toString(36).slice(2, 11);

export const initialOcrState: OcrState = {
  file: null,
  fileName: null,
  previewUrl: null,
  sourceId: '',
  customerCode: null,
  customerAbbr: null,
  siteCode: null,
  siteName: null,
  siteAbbr: null,
  ocrSiteName: null,
  customerNameEditable: false,
  siteNameEditable: false,
  header: {},
  headerOriginal: {},
  rows: [],
  rowsOriginal: [],
  editedFields: new Set(),
  matches: {},
  matchesOriginal: {},
  confirmedRows: new Set(),
  ocrNameRows: new Set(),
  ocrNameRowsOriginal: new Set(),
  headerConfirmed: false,
  phase: 'idle',
  sortKey: null,
  sortAsc: true,
};

export function ocrReducer(state: OcrState, action: OcrAction): OcrState {
  switch (action.type) {
    case 'FILE_SELECTED':
      return {
        ...initialOcrState,
        file: action.file,
        fileName: action.file.name,
        previewUrl: action.previewUrl,
      };

    case 'UPLOAD_LOADED':
      return {
        ...initialOcrState,
        fileName: action.fileName,
        previewUrl: action.previewUrl,
      };

    case 'OCR_START':
      return { ...state, phase: 'ocr_running', headerConfirmed: false };

    case 'OCR_DONE': {
      const rows: Row[] = action.items.map((it) => ({
        id: uuid(),
        line_no: it.line_no,
        row_type: it.row_type,
        product_name: it.product_name,
        size: it.size,
        quantity: it.quantity,
        unit: it.unit,
        irisu: it.irisu,
        // 一時保存からの復元では保存済み個数を優先（初回OCRは数量と同値）
        kosu: it.kosu ?? it.quantity,
        note: it.note,
        // 一時保存で編集済みの明細備考を復元（初回OCRはnoteをフォールバック）。
        // ただし備考(note)行はテキストを商品名へ寄せる方針のため、note を明細備考へは引き継がない。
        item_note: it.row_type === 'note' ? (it.item_note ?? '') : (it.item_note ?? it.note ?? ''),
        item_note2: it.item_note2 ?? '',
        // 備考/小計行の手入力商品名（初回OCRは未設定）。
        // 備考(note)行は商品名1が空だと ERP書込みが不可(商品名なしはNG)になるため、
        // 未設定時は備考テキスト(note)を商品名1の既定値にする（商品名・備考の両方に入る）。
        // 備考行は note を既定値にするが、空文字にはしない（空文字だと下流の
        // `note_name1 ?? product_name` フォールバックが効かず商品名1が空表示になる）。
        note_name1: it.note_name1 || (it.row_type === 'note' ? (it.note || undefined) : undefined),
        note_name2: it.note_name2,
        // メーカー見積のOCR価格・画面編集値を初期値に（見積依頼は空。画面で手入力可）
        list_price: it.list_price != null ? String(it.list_price) : '',
        estimate_unit_price: it.estimate_unit_price != null ? String(it.estimate_unit_price) : '',
        cost_price: it.cost_price != null ? String(it.cost_price) : '',
        // 一時保存からの復元で伝票分割を戻す（初回OCRは未設定＝分割なし）
        slip: it.slip_group,
      }));
      // ヘッダー確定済みなら得意先・現場・ヘッダーを維持
      const mergedHeader = {
        ...action.header,
        order_date: action.header.order_date || new Date().toISOString().slice(0, 10),
        note: action.header.note || '',
      };
      const h = action.header as Record<string, unknown>;
      const ocrSiteName = (h.site_name as string) || (h.project_name as string) || null;
      const headerFields = state.headerConfirmed
        ? { siteName: state.siteName || ocrSiteName, ocrSiteName }
        : {
            sourceId: action.sourceId,
            header: mergedHeader,
            headerOriginal: mergedHeader,
            customerCode: null,
            siteCode: null,
            siteName: ocrSiteName,
            ocrSiteName,
          };
      // 備考(区分3)・小計(区分4)は商品照合が不要なので、読込時点で確定済みにする。
      // これをしないと非明細行が黄色(未確定)のまま残り、allConfirmed が成立せず
      // ERP書込みボタンが無効になる（門田氏フィードバック Issue 1）。
      const confirmedRows = new Set<string>(
        rows.filter((r) => r.row_type && r.row_type !== 'detail').map((r) => r.id),
      );
      return {
        ...state,
        phase: 'ocr_done',
        ...headerFields,
        rows,
        rowsOriginal: rows.map((r) => ({ ...r })),
        editedFields: new Set(),
        matches: {},
        matchesOriginal: {},
        confirmedRows,
        ocrNameRows: new Set(),
  ocrNameRowsOriginal: new Set(),
        sortKey: null,
        sortAsc: true,
      };
    }

    case 'OCR_ERROR':
      return { ...state, phase: 'idle' };

    case 'CELL_EDIT': {
      const rows = state.rows.map((r) =>
        r.id === action.rowId ? { ...r, [action.field]: action.value } : r,
      );
      const orig = state.rowsOriginal.find((r) => r.id === action.rowId);
      const editedFields = new Set(state.editedFields);
      const key = `${action.rowId}:${action.field}`;
      if (orig && (orig as unknown as Record<string, unknown>)[action.field] !== action.value) {
        editedFields.add(key);
      } else {
        editedFields.delete(key);
      }
      // 品名・寸法の編集時のみ確定を解除（再検索が必要なため）
      const confirmedAfterCellEdit = new Set(state.confirmedRows);
      if ((action.field === 'product_name' || action.field === 'size') && confirmedAfterCellEdit.has(action.rowId)) {
        confirmedAfterCellEdit.delete(action.rowId);
      }
      return { ...state, rows, editedFields, confirmedRows: confirmedAfterCellEdit };
    }

    case 'HEADER_EDIT': {
      const header = { ...state.header, [action.field]: action.value };
      const editedFields = new Set(state.editedFields);
      // staff_code（マスタ自動取得）と input_staff_code（操作者入力）は修正ログの対象外
      const nonTrackFields: Array<keyof OcrHeader> = ['staff_code', 'input_staff_code'];
      if (!nonTrackFields.includes(action.field)) {
        const key = `header:${action.field}`;
        if (state.headerOriginal[action.field] !== action.value) {
          editedFields.add(key);
        } else {
          editedFields.delete(key);
        }
      }
      // 顧客名が変わったら sourceId も追従
      const sourceId = action.field === 'customer_name' ? action.value : state.sourceId;
      // input_staff_code / todoke_code の変更ではヘッダー確定を維持
      const keepConfirmed = action.field === 'input_staff_code' || action.field === 'todoke_code';
      const headerConfirmed = keepConfirmed ? state.headerConfirmed : false;
      // 届け先を「返品」(011)に切り替えたら、既存の個数・数量を一括で負数化する。
      // OCR取込済みの値も返品に揃える。返品以外への切替では何もしない。
      let rows = state.rows;
      if (action.field === 'todoke_code' && action.value === '011') {
        rows = state.rows.map((r) => {
          if (r.row_type === 'note') return r; // 備考行は数量を持たない
          const k = parseInt(r.kosu ?? r.quantity, 10);
          if (isNaN(k)) return r; // 空・非数の行はそのまま
          const newKosu = -Math.abs(k);
          const iri = parseInt(r.irisu ?? '1', 10) || 1;
          const newQuantity = String(newKosu * Math.abs(iri));
          if (String(newKosu) === (r.kosu ?? r.quantity) && newQuantity === r.quantity) return r;
          editedFields.add(`${r.id}:kosu`);
          editedFields.add(`${r.id}:quantity`);
          return { ...r, kosu: String(newKosu), quantity: newQuantity };
        });
      }
      return { ...state, header, rows, editedFields, sourceId, headerConfirmed };
    }

    case 'ROW_DELETE': {
      const rows = state.rows.map((r) =>
        r.id === action.rowId ? { ...r, isDeleted: true } : r,
      );
      const editedFields = new Set(state.editedFields);
      editedFields.add(`${action.rowId}:_deleted`);
      return { ...state, rows, editedFields };
    }

    case 'ROW_REMOVE': {
      const rows = state.rows.filter((r) => r.id !== action.rowId);
      const editedFields = new Set(state.editedFields);
      editedFields.delete(`${action.rowId}:_deleted`);
      editedFields.add(`${action.rowId}:_removed`);
      const confirmedRows = new Set(state.confirmedRows);
      confirmedRows.delete(action.rowId);
      const { [action.rowId]: _, ...matches } = state.matches;
      const { [action.rowId]: __, ...matchesOriginal } = state.matchesOriginal;
      return { ...state, rows, editedFields, confirmedRows, matches, matchesOriginal };
    }

    case 'ROW_RESTORE': {
      const orig = state.rowsOriginal.find((r) => r.id === action.rowId);
      const rows = state.rows.map((r) =>
        r.id === action.rowId
          ? orig
            ? { ...orig }
            : { ...r, isDeleted: false }
          : r,
      );
      const editedFields = new Set(state.editedFields);
      editedFields.delete(`${action.rowId}:_deleted`);
      return { ...state, rows, editedFields };
    }

    case 'ROW_ADD': {
      // 追加行は最初から数量系(入数=1・個数=1・数量=個数×入数=1)を入れる。
      // これをしないと個数・数量が空のままとなり、ERP書込みで「個数が未入力」でブロックされる。
      const newRow: Row = {
        id: uuid(),
        line_no: null,
        product_name: '',
        size: '',
        quantity: '1',
        unit: '',
        irisu: '1',
        kosu: '1',
        isAdded: true,
        afterRowId: action.afterRowId,
        // 追加行は上の行と同じ伝票に入れる
        slip: state.rows.find((r) => r.id === action.afterRowId)?.slip,
      };
      const idx = state.rows.findIndex((r) => r.id === action.afterRowId);
      const rows = [...state.rows];
      rows.splice(idx + 1, 0, newRow);
      const editedFields = new Set(state.editedFields);
      editedFields.add(`${newRow.id}:_added`);
      return { ...state, rows, editedFields };
    }

    case 'ROW_TYPE_SET': {
      const rows = state.rows.map((r) =>
        r.id === action.rowId ? { ...r, row_type: action.value } : r,
      );
      const orig = state.rowsOriginal.find((r) => r.id === action.rowId);
      const editedFields = new Set(state.editedFields);
      const key = `${action.rowId}:row_type`;
      if ((orig?.row_type ?? 'detail') !== action.value) {
        editedFields.add(key);
      } else {
        editedFields.delete(key);
      }
      // 備考(区分3)・小計(区分4)は商品照合不要なので確定扱いにする。
      // 明細(区分0)に戻す場合はマッチが無ければ未確定に戻す(再検索が必要)。
      const confirmedRows = new Set(state.confirmedRows);
      if (action.value === 'detail') {
        if (!state.matches[action.rowId]?.matchedId) confirmedRows.delete(action.rowId);
      } else {
        confirmedRows.add(action.rowId);
      }
      return { ...state, rows, editedFields, confirmedRows };
    }

    case 'CORRECTIONS_SAVED': {
      // 削除された行は実際に除去、追加行は通常行扱いにする
      const rows = state.rows
        .filter((r) => !r.isDeleted)
        .map((r) => ({ ...r, isAdded: false, afterRowId: undefined }));
      // 元データを現在の行で更新
      const rowsOriginal = rows.map((r) => ({ ...r }));
      return {
        ...state,
        rows,
        rowsOriginal,
        headerOriginal: state.header,
        editedFields: new Set(),
        matchesOriginal: { ...state.matches },
        ocrNameRowsOriginal: new Set(state.ocrNameRows),
      };
    }

    case 'CUSTOMER_SET': {
      const header = {
        ...state.header,
        customer_name: action.name,
        staff_code: action.staffCode ?? state.header.staff_code,
      };
      return {
        ...state,
        header,
        sourceId: action.name,
        customerCode: action.code,
        customerAbbr: action.abbr ?? null,
        customerNameEditable: action.nameEditable ?? false,
        siteCode: null, siteName: null, siteAbbr: null, siteNameEditable: false, // 得意先変更時にリセット
        headerConfirmed: false,
      };
    }

    case 'SITE_SET':
      return { ...state, siteCode: action.code, siteName: action.name ?? null, siteAbbr: action.abbr ?? null, siteNameEditable: action.nameEditable ?? false };

    case 'HEADER_CONFIRM': {
      // ヘッダー確定時点を基準にする（editedFields のヘッダー差分をクリア）
      const cleanedFields = new Set(state.editedFields);
      for (const key of cleanedFields) {
        if (key.startsWith('header:')) cleanedFields.delete(key);
      }
      return {
        ...state,
        headerConfirmed: true,
        headerOriginal: { ...state.header },
        editedFields: cleanedFields,
      };
    }

    case 'MATCH_SET': {
      // マッチ時に自動確定（空マッチはスキップ）
      const confirmedAfterMatch = new Set(state.confirmedRows);
      if (action.match.matchedId) {
        confirmedAfterMatch.add(action.rowId);
      } else {
        confirmedAfterMatch.delete(action.rowId);
      }
      // 初回の検索結果を記憶（変更検出用）
      const matchesOriginal = { ...state.matchesOriginal };
      if (!(action.rowId in matchesOriginal)) {
        matchesOriginal[action.rowId] = action.match;
      }
      return {
        ...state,
        matches: { ...state.matches, [action.rowId]: action.match },
        matchesOriginal,
        confirmedRows: confirmedAfterMatch,
      };
    }

    case 'ROW_CONFIRM': {
      const confirmedRows = new Set(state.confirmedRows);
      confirmedRows.add(action.rowId);
      return { ...state, confirmedRows };
    }

    case 'TOGGLE_OCR_NAME': {
      const ocrNameRows = new Set(state.ocrNameRows);
      if (ocrNameRows.has(action.rowId)) {
        ocrNameRows.delete(action.rowId);
      } else {
        ocrNameRows.add(action.rowId);
      }
      return { ...state, ocrNameRows };
    }

    case 'SYNC_OCR_NAME_ORIGINAL':
      return { ...state, ocrNameRowsOriginal: new Set(state.ocrNameRows) };

    case 'SORT_ROWS': {
      // ERP書込み(注文詳細)と同じ挙動: 同じ列を再クリックで昇順/降順トグル、
      // 別列なら昇順。No.(line_no)は数値、それ以外は日本語ロケールの文字列比較。
      const key = action.key;
      const asc = state.sortKey === key ? !state.sortAsc : true;
      // 「商品名1」列はマッチ結果由来(Row に直接の値が無い)。OCR名採用行は品名を、
      // それ以外はマッチ商品名を、実際の表示に合わせて比較キーにする。
      // 「商品コード」列も同じくマッチ結果由来。画面に出しているERP書込み用のコードで比べる。
      const sortValue = (r: Row): string =>
        key === 'matched_name1'
          ? (state.ocrNameRows.has(r.id) ? r.product_name : (state.matches[r.id]?.matchedName ?? ''))
          : key === 'product_code'
            ? erpProductCodeOf(state, r)
            : String((r as unknown as Record<string, unknown>)[key] ?? '');
      const cmp = (a: Row, b: Row): number => {
        // 伝票分割している場合は伝票の中だけで並べ替える（伝票をまたいで行を混ぜない）
        const bySlip = (a.slip ?? 1) - (b.slip ?? 1);
        if (bySlip !== 0) return bySlip;
        const c =
          key === 'line_no'
            ? (Number(a.line_no) || 0) - (Number(b.line_no) || 0)
            : sortValue(a).localeCompare(sortValue(b), 'ja');
        return asc ? c : -c;
      };
      // 明細行(削除行を除く)のみを並べ替え、備考/小計/削除行は元の位置に固定する。
      const detailIdx: number[] = [];
      state.rows.forEach((r, i) => {
        if ((r.row_type ?? 'detail') === 'detail' && !r.isDeleted) detailIdx.push(i);
      });
      const sortedDetails = detailIdx.map((i) => state.rows[i]).sort(cmp);
      const rows = [...state.rows];
      detailIdx.forEach((i, k) => { rows[i] = sortedDetails[k]; });
      return { ...state, rows, sortKey: key, sortAsc: asc };
    }

    case 'MOVE_ROW': {
      // 明細行(detail・非削除)のみを対象に、対象行を1つ上/下の明細行と入れ替える。
      // 備考/小計/削除行は位置を固定する（SORT_ROWS と同じ扱い）。
      // 手動並べ替えは列ソートより優先するため sortKey を解除する。
      const detailIdx: number[] = [];
      state.rows.forEach((r, i) => {
        if ((r.row_type ?? 'detail') === 'detail' && !r.isDeleted) detailIdx.push(i);
      });
      const pos = detailIdx.findIndex((i) => state.rows[i].id === action.rowId);
      if (pos < 0) return state;
      const swap = action.direction === 'up' ? pos - 1 : pos + 1;
      if (swap < 0 || swap >= detailIdx.length) return state; // 端では何もしない
      const rows = [...state.rows];
      const a = detailIdx[pos];
      const b = detailIdx[swap];
      // 隣が別の伝票なら入れ替えず、その伝票へ移す（区切りを1行ずらすのと同じ）。
      // 入れ替えると伝票の並びが崩れ、区切りの位置と伝票の中身が食い違う。
      if ((rows[a].slip ?? 1) !== (rows[b].slip ?? 1)) {
        rows[a] = { ...rows[a], slip: rows[b].slip };
        return { ...state, rows: withoutEmptySlips(rows), sortKey: null };
      }
      [rows[a], rows[b]] = [rows[b], rows[a]];
      return { ...state, rows, sortKey: null };
    }

    case 'MOVE_ROW_TO': {
      // ドラッグ&ドロップ: 対象行を、落とした先の明細行の位置へ動かす。
      // MOVE_ROW と同じく明細行(detail・非削除)だけを並べ替え、備考/小計/削除行は位置を固定する。
      // 下へ動かすときは落とした行の後ろ、上へ動かすときは前に入る。
      // 落とした先の伝票へ移す（伝票をまたいでも区切りの位置と中身が食い違わない）。
      const detailIdx: number[] = [];
      state.rows.forEach((r, i) => {
        if ((r.row_type ?? 'detail') === 'detail' && !r.isDeleted) detailIdx.push(i);
      });
      const from = detailIdx.findIndex((i) => state.rows[i].id === action.rowId);
      const to = detailIdx.findIndex((i) => state.rows[i].id === action.targetId);
      if (from < 0 || to < 0 || from === to) return state;
      const details = detailIdx.map((i) => state.rows[i]);
      const [moved] = details.splice(from, 1);
      details.splice(to, 0, { ...moved, slip: state.rows[detailIdx[to]].slip });
      const rows = [...state.rows];
      detailIdx.forEach((i, k) => { rows[i] = details[k]; });
      return { ...state, rows: withoutEmptySlips(rows), sortKey: null };
    }

    case 'SLIP_SPLIT': {
      // この行の次の行から後ろ（同じ伝票以降）を次の伝票にする。
      // 「+」(下に行を追加)と同じく、押した行の下に作用させる（三興バルブ様の要望）。
      const at = state.rows.findIndex((r) => r.id === action.rowId);
      if (at < 0) return state;
      const idx = state.rows.findIndex((r, i) => i > at && !r.isDeleted);
      if (idx < 0) return state; // 最後の行の下には区切れない
      const k = state.rows[idx].slip ?? 1;
      if (k !== (state.rows[at].slip ?? 1)) return state; // すでに区切りがある
      const rows = state.rows.map((r, i) =>
        i >= idx && (r.slip ?? 1) >= k ? { ...r, slip: (r.slip ?? 1) + 1 } : r,
      );
      const editedFields = new Set(state.editedFields);
      editedFields.add(`${action.rowId}:_slip`);
      // 分割した注文では相手伝票No.を送らない（ERPが2枚目以降を重複で拒否するため）。
      // 画面の値も空にして、入力が残っているのに送られない状態を作らない。
      let header = state.header;
      if (header.slip_no) {
        header = { ...header, slip_no: '' };
        editedFields.add('header:slip_no');
      }
      return { ...state, rows, editedFields, header };
    }

    case 'SLIP_MERGE': {
      // この行が先頭の伝票を、1つ前の伝票に戻す（区切りの取り消し）。
      // 間の伝票が削除で空になっていることがあるので、戻し先は「画面に出ている」1つ前の伝票。
      const idx = state.rows.findIndex((r) => r.id === action.rowId);
      if (idx < 0) return state;
      const k = state.rows[idx].slip ?? 1;
      const prevVisible = state.rows.slice(0, idx).reverse().find((r) => !r.isDeleted);
      const p = prevVisible?.slip ?? 1;
      if (k <= p) return state;
      const rows = state.rows.map((r) => {
        const v = r.slip ?? 1;
        if (v > p && v <= k) return { ...r, slip: p };
        if (v > k) return { ...r, slip: v - (k - p) };
        return r;
      });
      const editedFields = new Set(state.editedFields);
      editedFields.add(`${action.rowId}:_slip`);
      return { ...state, rows, editedFields };
    }

    case 'SAVE_START':
      return { ...state, phase: 'saving' };

    case 'SAVE_DONE':
      return { ...state, phase: 'ocr_done' };

    case 'RESET':
      return initialOcrState;

    default:
      return state;
  }
}

/** 行が1つも無くなった伝票の番号を詰める（削除行も数える＝戻したときに行き場がある）。 */
function withoutEmptySlips(rows: Row[]): Row[] {
  const used = [...new Set(rows.map((r) => r.slip ?? 1))].sort((a, b) => a - b);
  const renum = new Map(used.map((v, i) => [v, i + 1]));
  return rows.map((r) => (renum.get(r.slip ?? 1) === (r.slip ?? 1) ? r : { ...r, slip: renum.get(r.slip ?? 1) }));
}

export interface SlipInfo {
  /** 伝票の数。分割していなければ 1 */
  count: number;
  /** 行ID → 伝票番号(1..N)。削除行は含まない */
  groupOf: Record<string, number>;
  /** 伝票ごとの行数（index 0 = 伝票1） */
  sizes: number[];
}

/** 画面に出ている行から伝票の区切りを求める。番号は出現順に 1..N へ振り直す
 * （削除で空になった伝票は数えない）。バックエンドの slip_groups と同じ考え方。 */
export function slipInfo(rows: Row[]): SlipInfo {
  const renum = new Map<number, number>();
  const groupOf: Record<string, number> = {};
  const sizes: number[] = [];
  for (const r of rows) {
    if (r.isDeleted) continue;
    const raw = r.slip ?? 1;
    if (!renum.has(raw)) renum.set(raw, renum.size + 1);
    const k = renum.get(raw)!;
    groupOf[r.id] = k;
    sizes[k - 1] = (sizes[k - 1] ?? 0) + 1;
  }
  return { count: Math.max(1, renum.size), groupOf, sizes };
}

// ── 派生値 ──
export const selectors = {
  hasUnsavedChanges: (s: OcrState) => s.editedFields.size > 0,

  visibleRows: (s: OcrState) => s.rows.filter((r) => !r.isDeleted),

  allConfirmed: (s: OcrState) => {
    const visible = s.rows.filter((r) => !r.isDeleted);
    // 明細(detail)以外の行(備考/小計/中計)は商品を持たないため確定(マッチ=商品コード)は不要。
    // 明細行のみ確定を要求する（ボタン活性・保存時チェックと整合）。
    return (
      visible.length > 0 &&
      visible.every(
        (r) => (r.row_type && r.row_type !== 'detail') || s.confirmedRows.has(r.id),
      )
    );
  },

  isCellEdited: (s: OcrState, rowId: string, field: string) =>
    s.editedFields.has(`${rowId}:${field}`),

  isHeaderEdited: (s: OcrState, field: string) => s.editedFields.has(`header:${field}`),
};
